import { IAdvertiserGridRow } from '@/Interfaces/Advertiser.interface';
import ConfirmationModal from '@/components/Modal/ConfirmationModal';
import { wait } from '@/helpers/common';
import { useAppDispatch } from '@/store/hooks';
import { archiveAdvertiser } from '@/store/slices/advertisers.slice';
import { getClients, getWhitelabels } from '@/store/slices/sharedApi.slice';
import { Button, SxProps, Theme } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

interface IProps {
  row: IAdvertiserGridRow;
  sx?: SxProps<Theme>;
}

const ArchiveButton = ({ sx, row }: IProps) => {
  const dispatch = useAppDispatch();

  const [isOpenedConfirmation, setIsOpenedConfirmation] = useState(false);

  const onConfirm = useCallback(async () => {
    try {
      await dispatch(
        archiveAdvertiser({
          advertiserId: row.id,
          isArchived: !row.isArchived,
        }),
      ).unwrap();

      await wait(200);
      dispatch(getClients({ refresh: true }));
      dispatch(getWhitelabels({ refresh: true }));
    } catch (error) {
      toast.error((error as Error).message || 'Something went wrong');
    } finally {
      setIsOpenedConfirmation(false);
    }
  }, [dispatch, row]);

  const confirmationText = useMemo(() => {
    return `Are you sure you want to ${row.isArchived ? 'unarchive' : 'archive'} this advertiser?`;
  }, [row]);

  const onActionClick = useCallback(() => {
    setIsOpenedConfirmation(true);
  }, []);

  const onConfirmationClose = useCallback(() => {
    setIsOpenedConfirmation(false);
  }, []);

  return (
    <>
      <Button
        variant='outlined'
        color='primary'
        sx={{
          minHeight: '29px',
          padding: 0,
          paddingX: '8px',
          marginLeft: '8px',
          borderRadius: '32px',
          whiteSpace: 'nowrap',
          minWidth: 'unset',
          ...sx,
        }}
        onClick={onActionClick}
      >
        {row.isArchived ? 'Unarchive' : 'Archive'}
      </Button>
      {isOpenedConfirmation && (
        <ConfirmationModal
          open={isOpenedConfirmation}
          onClose={onConfirmationClose}
          onConfirm={onConfirm}
          text={confirmationText}
        />
      )}
    </>
  );
};

export default ArchiveButton;
